import { useCallback, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, EnrichedRepriceRequest, FullProject, ProjectService } from 'entities';

import { Skeleton, Column, LabeledSelect } from 'components/Blocks';

import { useProjectUpdate } from 'providers/Project';
import { usePendingActions } from 'providers/PendingActions';

import { SingleReprice } from './SingleReprice';

import { parseProjectsRequest } from './utils';

import { omit } from 'utils/object';

export interface ProjectWithRepriceRequestParse extends Omit<FullProject, 'services'> {
  requestData: Omit<EnrichedRepriceRequest, 'request'>;

  currentServices: ProjectService[];

  services: Array<{
    id: string;
    name: string;
    service: string;
    outsourceConfig: ProjectService['outsourceConfig'];

    costs: Array<{
      country: string;
      justification: string;
      costs: ProjectService['costs'][string];
    }>;
  }>;
}

export function RepriceRequest() {
  const { t } = useLang();

  const updateProject = useProjectUpdate();
  const { decreaseCount } = usePendingActions();

  const [focusedProject, setFocusedProject] = useState('');

  const {
    data: repriceProjects,
    loading: loadingProjects,
    overwrite,
  } = loaders.projects.listActiveReprices.use({
    defaultValue: {},

    refreshMinutes: 5,

    onDataChange: (mapping) => {
      const [firstId] = Object.keys(mapping);

      setFocusedProject((old) => old || firstId || '');
    },
  });

  const removeProject = useCallback(
    (id: string) => {
      const [firstId] = Object.keys(repriceProjects).filter((key) => key !== id);
      setFocusedProject(firstId || '');

      overwrite((old) => omit(old, [id]));

      updateProject(id, { reprice: 'PENDING_APPROVAL' });

      decreaseCount('PROJECT_REPRICE_REQUESTS');
    },
    [updateProject, repriceProjects, overwrite, decreaseCount],
  );

  const options = useMemo(() => {
    return parseProjectsRequest(repriceProjects);
  }, [repriceProjects]);

  if (loadingProjects && !focusedProject)
    return (
      <div className="mx-auto w-full max-w-[850px] pb-4">
        <Skeleton height={130} quantity={3} />
      </div>
    );

  if (!options.length)
    return (
      <div className="mx-auto w-full max-w-[850px] pb-4">
        <p className="text-center text-lg font-medium">
          {t('no-project-requires-repricing-right-now')}
        </p>
      </div>
    );

  const selectedProject = options.find(
    ({ id }) => focusedProject === id,
  ) as ProjectWithRepriceRequestParse;

  return (
    <Column className="mx-auto w-full max-w-[850px] pb-4">
      <LabeledSelect
        className="justify-center [&>div]:w-fit"
        id="repricing-projects"
        options={options}
        value={focusedProject}
        onChangeDirect={setFocusedProject}
        label={t('project')}
        isSearchable
        labelKey="name"
      />

      {!!selectedProject && (
        <SingleReprice
          key={`reprice-${selectedProject.id}`}
          project={selectedProject}
          removeSelf={() => removeProject(selectedProject.id)}
        />
      )}
    </Column>
  );
}
